import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material';
import { updateAttendee, useActor } from '../../lib/audited-writes';
import { useToast } from '../../lib/toast-context';
import { useUserContext } from '../../lib/users-context';
import { Attendee, Group } from '../../lib/types';
import { formatGroupShort, sortGroups } from '../../lib/groups';

const MoveAttendeeDialog = ({
  attendee,
  groups,
  onClose,
}: {
  attendee: Attendee | null;
  groups: Group[];
  onClose: () => void;
}) => {
  const actor = useActor();
  const toast = useToast();
  const { canMoveAttendees } = useUserContext();
  const [targetRef, setTargetRef] = useState('');
  const [working, setWorking] = useState(false);

  const sortedGroups = useMemo(() => sortGroups(groups), [groups]);

  const otherGroups = useMemo(
    () =>
      attendee
        ? sortedGroups.filter((g) => g.ref !== attendee.groupRef)
        : sortedGroups,
    [sortedGroups, attendee],
  );

  const handleClose = () => {
    if (working) return;
    setTargetRef('');
    onClose();
  };

  const handleMove = async () => {
    if (!attendee || !targetRef || working) return;
    // Defence-in-depth: the Roster only renders the Move button for
    // users with `canMoveAttendees`, so the dialog should only ever
    // open for them. Re-check here so a stale React state / future
    // entry point can't bypass the gate.
    if (!canMoveAttendees) {
      toast.error('You do not have permission to move attendees');
      onClose();
      return;
    }
    setWorking(true);
    try {
      await updateAttendee(actor, attendee, { groupRef: targetRef });
      toast.success(
        `Moved ${attendee.first_name} ${attendee.last_name} to ${formatGroupShort(
          sortedGroups.find((g) => g.ref === targetRef)!,
        )}`,
      );
      setTargetRef('');
      onClose();
    } catch (error) {
      console.error('Error moving attendee', error);
      toast.error('Failed to move attendee');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={Boolean(attendee)} onClose={handleClose} fullWidth maxWidth='xs'>
      <DialogTitle>Move attendee</DialogTitle>
      <DialogContent>
        {attendee && (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              Move <strong>{attendee.first_name} {attendee.last_name}</strong> to
              a different group.
            </DialogContentText>
            <FormControl fullWidth size='small'>
              <InputLabel id='move-group-label'>Group</InputLabel>
              <Select
                labelId='move-group-label'
                label='Group'
                value={targetRef}
                onChange={(e: SelectChangeEvent<string>) =>
                  setTargetRef(e.target.value)
                }
              >
                {otherGroups.map((g) => (
                  <MenuItem key={g.ref} value={g.ref}>
                    {formatGroupShort(g)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={working}>
          Cancel
        </Button>
        <Button
          onClick={handleMove}
          variant='contained'
          disabled={working || !targetRef}
        >
          Move
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveAttendeeDialog;
